import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSalon } from '../contexts/SalonContext';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { FaBuilding, FaKey, FaArrowRight, FaArrowLeft, FaHourglassHalf, FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';
import './UserOnboardingDashboard.css';
import { apiService } from '../services/api';

const API = apiService.getBaseUrl();

// ============================================================
// FEATURE FLAG: Tắt admin approval để Apple App Store review
// true  = tạo business trực tiếp (không cần admin duyệt)
// false = flow bình thường (gửi request → admin duyệt → verify code)
// TODO: Đổi thành false sau khi app được Apple approve và public
// ============================================================
const BYPASS_ADMIN_APPROVAL = true;

type ViewMode = 'select' | 'create' | 'join' | 'pending' | 'verify';

interface FormMessage {
    type: 'error' | 'success';
    text: string;
}

const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken');
    return {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
};

const UserOnboardingDashboard: React.FC = () => {
    const { user, logout } = useAuth();
    const { t, language } = useLanguage();
    const navigate = useNavigate();

    const [viewMode, setViewMode] = useState<ViewMode>('select');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState<FormMessage | null>(null);

    // Create Business Form
    const [businessName, setBusinessName] = useState('');
    const [businessDescription, setBusinessDescription] = useState('');
    const [businessPhone, setBusinessPhone] = useState('');
    const [businessAddress, setBusinessAddress] = useState('');

    // Join Business Form
    const [businessCode, setBusinessCode] = useState('');

    // Pending / Verify state
    const [requestId, setRequestId] = useState<string | null>(null);
    const [requestStatus, setRequestStatus] = useState<string>('pending');
    const [verifyCode, setVerifyCode] = useState('');
    const [codeExpiry, setCodeExpiry] = useState<Date | null>(null);
    const [timeLeft, setTimeLeft] = useState<number>(0);

    const T = (en: string, vi: string) => language === 'vi' ? vi : en;

    // Poll my request status every 5s when on pending screen
    const pollMyRequest = useCallback(async () => {
        if (!requestId) return;
        try {
            const res = await fetch(`${API}/business-requests/my`, { headers: getAuthHeaders() });
            const data = await res.json();
            if (data.success && data.data) {
                setRequestStatus(data.data.status);
                if (data.data.verificationExpiry) {
                    setCodeExpiry(new Date(data.data.verificationExpiry));
                }
                if (data.data.status === 'code_sent') {
                    // Show verify button
                }
            }
        } catch { /* silent */ }
    }, [requestId]);

    useEffect(() => {
        if (viewMode !== 'pending' || !requestId) return;
        pollMyRequest();
        const interval = setInterval(pollMyRequest, 5000);
        return () => clearInterval(interval);
    }, [viewMode, requestId, pollMyRequest]);

    // Countdown timer for code expiry
    useEffect(() => {
        if (!codeExpiry) { setTimeLeft(0); return; }
        const tick = () => {
            const left = Math.max(0, Math.floor((codeExpiry.getTime() - Date.now()) / 1000));
            setTimeLeft(left);
            if (left === 0) {
                setRequestStatus('pending');
                setCodeExpiry(null);
            }
        };
        tick();
        const id = setInterval(tick, 1000);
        return () => clearInterval(id);
    }, [codeExpiry]);

    // Check for existing pending request on mount
    useEffect(() => {
        const check = async () => {
            try {
                const res = await fetch(`${API}/business-requests/my`, { headers: getAuthHeaders() });
                const data = await res.json();
                if (data.success && data.data) {
                    setRequestId(data.data._id);
                    setRequestStatus(data.data.status);
                    if (data.data.verificationExpiry) setCodeExpiry(new Date(data.data.verificationExpiry));
                    setViewMode('pending');
                }
            } catch { /* silent */ }
        };
        check();
    }, []);

    const resetForms = () => {
        setBusinessName(''); setBusinessDescription('');
        setBusinessPhone(''); setBusinessAddress('');
        setBusinessCode(''); setMessage(null);
    };

    const handleBack = () => { setViewMode('select'); resetForms(); };

    const handleCreateBusiness = async (e: React.FormEvent) => {
        e.preventDefault();
        setMessage(null);
        if (!businessName.trim()) {
            setMessage({ type: 'error', text: T('Business name is required', 'Vui lòng nhập tên business') });
            return;
        }
        setLoading(true);
        try {
            if (BYPASS_ADMIN_APPROVAL) {
                // ===== TẠM THỜI: Tạo business trực tiếp (không cần admin) =====
                const res = await fetch(`${API}/businesses`, {
                    method: 'POST',
                    headers: getAuthHeaders(),
                    body: JSON.stringify({
                        name: businessName.trim(),
                        description: businessDescription.trim(),
                        phone: businessPhone.trim(),
                        address: businessAddress.trim(),
                    }),
                });
                const data = await res.json();
                if (data.success) {
                    setMessage({ type: 'success', text: T('Business created successfully!', 'Tạo business thành công!') });
                    setTimeout(() => window.location.reload(), 1500);
                } else {
                    setMessage({ type: 'error', text: data.message || T('Failed to create business', 'Tạo business thất bại') });
                }
            } else {
                // ===== FLOW GỐC: Gửi request → chờ admin duyệt =====
                const res = await fetch(`${API}/business-requests`, {
                    method: 'POST',
                    headers: getAuthHeaders(),
                    body: JSON.stringify({
                        businessName: businessName.trim(),
                        description: businessDescription.trim(),
                        phone: businessPhone.trim(),
                        address: businessAddress.trim(),
                    }),
                });
                const data = await res.json();
                if (data.success) {
                    setRequestId(data.requestId);
                    setRequestStatus('pending');
                    setViewMode('pending');
                } else if (res.status === 409) {
                    setRequestId(data.requestId);
                    setRequestStatus(data.status);
                    setViewMode('pending');
                } else {
                    setMessage({ type: 'error', text: data.message || T('Submit failed', 'Gửi yêu cầu thất bại') });
                }
            }
        } catch {
            setMessage({ type: 'error', text: T('Connection error. Please try again.', 'Lỗi kết nối. Vui lòng thử lại.') });
        } finally {
            setLoading(false);
        }
    };

    const handleJoinBusiness = async (e: React.FormEvent) => {
        e.preventDefault();
        setMessage(null);
        const code = businessCode.trim();
        if (!code) {
            setMessage({ type: 'error', text: t('onboarding.joinForm.validationCode') });
            return;
        }
        setLoading(true);
        try {
            const response = await fetch(`${API}/businesses/join`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ businessId: code }),
            });
            const data = await response.json();
            if (data.success) {
                setMessage({ type: 'success', text: t('onboarding.joinForm.success') });
                setTimeout(() => window.location.reload(), 1500);
            } else {
                throw new Error(data.message || t('onboarding.joinForm.errorInvalid'));
            }
        } catch (error: any) {
            setMessage({ type: 'error', text: error.message || t('onboarding.joinForm.errorDefault') });
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyCode = async (e: React.FormEvent) => {
        e.preventDefault();
        setMessage(null);
        if (!verifyCode.trim()) {
            setMessage({ type: 'error', text: T('Please enter the code', 'Vui lòng nhập mã xác nhận') });
            return;
        }
        setLoading(true);
        try {
            const res = await fetch(`${API}/business-requests/verify`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ code: verifyCode.trim(), requestId }),
            });
            const data = await res.json();
            if (data.success) {
                setMessage({ type: 'success', text: T('🎉 Business created! Redirecting to Dashboard...', '🎉 Business đã được tạo! Đang vào Dashboard...') });
                setTimeout(() => { window.location.href = '/dashboard'; }, 1500);
            } else if (data.expired) {
                setMessage({ type: 'error', text: T('Code expired. Admin will send a new one.', 'Mã đã hết hạn. Admin sẽ gửi mã mới.') });
                setRequestStatus('pending');
                setCodeExpiry(null);
                setVerifyCode('');
            } else {
                setMessage({ type: 'error', text: data.message + (data.debug ? ` [${data.debug}]` : '') });
            }
        } catch {
            setMessage({ type: 'error', text: T('Connection error', 'Lỗi kết nối') });
        } finally {
            setLoading(false);
        }
    };

    // ── Select View ──
    if (viewMode === 'select') {
        return (
            <div className="onboarding-dashboard">
                <div className="onboarding-container">
                    <div className="onboarding-header">
                        <h1>{t('onboarding.welcome')}</h1>
                        <p>{t('onboarding.subtitle')}</p>
                    </div>
                    <div className="onboarding-cards">
                        <div className="onboarding-card" onClick={() => setViewMode('create')}>
                            <div className="card-icon create"><FaBuilding /></div>
                            <h3>{t('onboarding.createCard.title')}</h3>
                            <p>{t('onboarding.createCard.desc')}</p>
                            <span className="card-action">{t('onboarding.createCard.action')} <FaArrowRight /></span>
                        </div>
                        <div className="onboarding-card" onClick={() => setViewMode('join')}>
                            <div className="card-icon join"><FaKey /></div>
                            <h3>{t('onboarding.joinCard.title')}</h3>
                            <p>{t('onboarding.joinCard.desc')}</p>
                            <span className="card-action">{t('onboarding.joinCard.action')} <FaArrowRight /></span>
                        </div>
                    </div>
                    <div className="user-info">
                        <p>{t('onboarding.loggedInAs')} <span className="email">{user?.email}</span>
                            <span className="logout-link" onClick={logout}>{t('onboarding.logout')}</span>
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    // ── Create Business Form ──
    if (viewMode === 'create') {
        return (
            <div className="onboarding-dashboard">
                <div className="onboarding-container">
                    <div className="onboarding-form">
                        <div className="form-header">
                            <button className="back-btn" onClick={handleBack}><FaArrowLeft /></button>
                            <h2>{T('Open Your Business', 'Mở Business của bạn')}</h2>
                        </div>
                        {message && (
                            <div className={`message ${message.type}`}>
                                {message.type === 'error' ? <FaExclamationCircle /> : <FaCheckCircle />}
                                {message.text}
                            </div>
                        )}
                        <form onSubmit={handleCreateBusiness}>
                            <div className="form-group">
                                <label>{t('onboarding.createForm.nameLabel')}</label>
                                <input type="text" placeholder={t('onboarding.createForm.namePlaceholder')}
                                    value={businessName} onChange={e => setBusinessName(e.target.value)}
                                    disabled={loading} autoFocus />
                            </div>
                            <div className="form-group">
                                <label>{t('onboarding.createForm.descLabel')}</label>
                                <textarea placeholder={t('onboarding.createForm.descPlaceholder')}
                                    value={businessDescription} onChange={e => setBusinessDescription(e.target.value)}
                                    disabled={loading} />
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>{t('onboarding.createForm.phoneLabel')}</label>
                                    <input type="tel" placeholder="0123 456 789"
                                        value={businessPhone} onChange={e => setBusinessPhone(e.target.value)}
                                        disabled={loading} />
                                </div>
                                <div className="form-group">
                                    <label>{t('onboarding.createForm.addressLabel')}</label>
                                    <input type="text" placeholder={t('onboarding.createForm.addressPlaceholder')}
                                        value={businessAddress} onChange={e => setBusinessAddress(e.target.value)}
                                        disabled={loading} />
                                </div>
                            </div>
                            <button type="submit" className="submit-btn" disabled={loading}>
                                {loading ? <><span className="loading-spinner" /> {T('Submitting...', 'Đang gửi...')}</> : <><FaBuilding /> {T('Submit Request', 'Gửi yêu cầu')}</>}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        );
    }

    // ── Join Business Form ──
    if (viewMode === 'join') {
        return (
            <div className="onboarding-dashboard">
                <div className="onboarding-container">
                    <div className="onboarding-form">
                        <div className="form-header">
                            <button className="back-btn" onClick={handleBack}><FaArrowLeft /></button>
                            <h2>{t('onboarding.joinForm.title')}</h2>
                        </div>
                        {message && (
                            <div className={`message ${message.type}`}>
                                {message.type === 'error' ? <FaExclamationCircle /> : <FaCheckCircle />}
                                {message.text}
                            </div>
                        )}
                        <form onSubmit={handleJoinBusiness}>
                            <div className="form-group">
                                <label>{t('onboarding.joinForm.codeLabel')}</label>
                                <input type="text" className="code-input"
                                    placeholder={t('onboarding.joinForm.codePlaceholder')}
                                    value={businessCode} onChange={e => setBusinessCode(e.target.value)}
                                    disabled={loading} autoFocus spellCheck={false}
                                    style={{ letterSpacing: '1px', fontSize: '14px' }} />
                                <p className="code-hint">{t('onboarding.joinForm.codeHint')}</p>
                            </div>
                            <button type="submit" className="submit-btn join-btn" disabled={loading}>
                                {loading ? <><span className="loading-spinner" /> {t('onboarding.joinForm.submitLoading')}</> : <><FaKey /> {t('onboarding.joinForm.submitBtn')}</>}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        );
    }

    // ── Pending Screen ──
    if (viewMode === 'pending') {
        const hasActiveCode = requestStatus === 'code_sent' && timeLeft > 0;
        return (
            <div className="onboarding-dashboard">
                <div className="onboarding-container pending-container">
                    <div className="pending-icon-wrap">
                        <FaHourglassHalf className="pending-hourglass" />
                    </div>
                    <h2 className="pending-title">
                        {T('Your request is under review', 'Yêu cầu của bạn đang được xem xét')}
                    </h2>
                    <p className="pending-subtitle">
                        {T(
                            'The process is pending review and implementation. You will receive an email with a verification code once approved.',
                            'Quy trình đang chờ được xem xét và thực hiện. Bạn sẽ nhận được email chứa mã xác nhận khi được phê duyệt.'
                        )}
                    </p>

                    {hasActiveCode && (
                        <div className="code-timer-banner">
                            <span>⏰</span>
                            <span>
                                {T('A verification code was sent to your email!', 'Mã xác nhận đã được gửi tới email của bạn!')}
                                {' '}<strong>{T('Expires in', 'Hết hạn sau')} {timeLeft}s</strong>
                            </span>
                        </div>
                    )}

                    {hasActiveCode && (
                        <button className="enter-code-btn" onClick={() => setViewMode('verify')}>
                            {T('Enter Verification Code →', 'Nhập mã xác nhận →')}
                        </button>
                    )}

                    {!hasActiveCode && requestStatus === 'code_sent' && (
                        <div className="expired-notice">
                            {T('⏱ The previous code expired. Waiting for admin to send a new one.', '⏱ Mã trước đã hết hạn. Đang chờ admin gửi mã mới.')}
                        </div>
                    )}

                    <p className="pending-polling">
                        {T('This page refreshes automatically every 5 seconds.', 'Trang này tự động cập nhật mỗi 5 giây.')}
                    </p>

                    <button className="back-link-btn" onClick={() => { setViewMode('select'); setRequestId(null); }}>
                        {T('← Start over', '← Bắt đầu lại')}
                    </button>
                </div>
            </div>
        );
    }

    // ── Verify Code Screen ──
    if (viewMode === 'verify') {
        return (
            <div className="onboarding-dashboard">
                <div className="onboarding-container">
                    <div className="onboarding-form">
                        <div className="form-header">
                            <button className="back-btn" onClick={() => setViewMode('pending')}><FaArrowLeft /></button>
                            <h2>{T('Enter Verification Code', 'Nhập mã xác nhận')}</h2>
                        </div>

                        {timeLeft > 0 ? (
                            <div className="code-timer-banner">
                                ⏰ {T('Code expires in', 'Mã hết hạn sau')} <strong>{timeLeft}s</strong>
                            </div>
                        ) : (
                            <div className="expired-notice">
                                {T('Code has expired. Please go back and wait for a new one.', 'Mã đã hết hạn. Vui lòng quay lại và chờ mã mới.')}
                            </div>
                        )}

                        {message && (
                            <div className={`message ${message.type}`}>
                                {message.type === 'error' ? <FaExclamationCircle /> : <FaCheckCircle />}
                                {message.text}
                            </div>
                        )}

                        <form onSubmit={handleVerifyCode}>
                            <div className="form-group">
                                <label>{T('Verification Code', 'Mã xác nhận')}</label>
                                <input
                                    type="text"
                                    className="code-input verify-code-input"
                                    placeholder={T('Enter code', 'Nhập mã')}
                                    value={verifyCode}
                                    onChange={e => setVerifyCode(e.target.value)}
                                    disabled={loading || timeLeft === 0}
                                    autoFocus
                                    maxLength={20}
                                    style={{ letterSpacing: '4px', fontSize: '22px', textAlign: 'center' }}
                                />
                                <p className="code-hint">
                                    {T('Enter the code sent to your email to activate your business.', 'Nhập mã được gửi tới email của bạn để kích hoạt business.')}
                                </p>
                            </div>
                            <button type="submit" className="submit-btn" disabled={loading || timeLeft === 0}>
                                {loading
                                    ? <><span className="loading-spinner" /> {T('Verifying...', 'Đang xác nhận...')}</>
                                    : <><FaCheckCircle /> {T('Verify & Create Business', 'Xác nhận & Tạo Business')}</>
                                }
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        );
    }

    return null;
};

export default UserOnboardingDashboard;
